import { Seo } from '@/components/Seo';
import { ContentPage, P } from '@/components/PublicLayout';
import { useLang, useT } from '@/i18n/LanguageContext';
import { recentContent, workImages, workOrder, workArchiveLinks } from '@/i18n/content/recent';
import { pathFor } from '@/i18n/routes';
import { Link } from 'react-router-dom';
import lit1 from '@/assets/lit1.png.asset.json';
import lit2 from '@/assets/lit2.png.asset.json';
import lit3 from '@/assets/lit3.png.asset.json';
import lit4 from '@/assets/lit4.png.asset.json';

const IMAGES = [lit1.url, lit2.url, lit3.url, lit4.url];

const ALTS: Record<'nl' | 'en', string[]> = {
  nl: [
    'Detail van een Perpetual Plexus lichtsculptuur met geometrische patronen',
    'Verlichte houten vensters van een Perpetual Plexus sculptuur',
    'Zwart-wit patroon van gelaagde panelen in het netwerk',
    'Ruimtelijk reliëf van concentrische vierkanten in licht en schaduw',
  ],
  en: [
    'Detail of a Perpetual Plexus light sculpture with geometric patterns',
    'Illuminated wooden windows of a Perpetual Plexus sculpture',
    'Black and white pattern of layered panels in the network',
    'Spatial relief of concentric squares in light and shadow',
  ],
};

export default function Recent() {
  const lang = useLang();
  const t = useT(recentContent);
  const archiefLink = (key: keyof typeof workArchiveLinks) =>
    `${pathFor('bio', lang)}?project=${encodeURIComponent(workArchiveLinks[key])}#archive`;

  const alts = ALTS[lang] ?? ALTS.en;

  const figure = (index: number) => {
    const right = index % 2 === 0;
    return (
      <figure
        className={
          right
            ? 'mb-6 sm:float-right sm:w-1/2 sm:ml-8'
            : 'mb-6 sm:float-left sm:w-1/2 sm:mr-8'
        }
      >
        <img
          src={IMAGES[index]}
          alt={alts[index]}
          loading="lazy"
          className="w-full aspect-square object-cover rounded-sm"
        />
      </figure>
    );
  };


  return (
    <>
      <Seo title={t.seoTitle} description={t.seoDescription} path={pathFor('recent', lang)} />
      <ContentPage title={t.title}>
        {t.centeredIntro && (
          <div className="my-10 text-center">
            {t.centeredIntro.map((line, i) => {
              if (typeof line === 'string') {
                return (
                  <p key={i} className="leading-relaxed text-sitefg/80">
                    {line}
                  </p>
                );
              }
              return (
                <p key={i} className="leading-relaxed text-sitefg/80">
                  {line.before}
                  <Link
                    to={pathFor('visualisatie', lang)}
                    className="text-[var(--viz-accent)] hover:underline"
                  >
                    {line.link}
                  </Link>
                  {line.after}
                </p>
              );
            })}
          </div>
        )}

        {t.lead.map((p, i) => (
          <div key={i} className="contents">
            {i === 1 && figure(0)}
            <P>{p}</P>
          </div>
        ))}

        <blockquote className="my-8 border-l-2 border-[var(--viz-accent)] pl-6 italic text-sitefg/70">
          {t.quote}
          <footer className="mt-2 not-italic text-xs font-mono tracking-wider text-sitefg/40">
            {t.quoteSource}
          </footer>
        </blockquote>

        {t.body.map((p, i) => (
          <div key={i} className="contents">
            {i === 0 && figure(1)}
            {i === 2 && figure(2)}
            {i === 4 && figure(3)}
            <P>{p}</P>
          </div>
        ))}
        <div className="clear-both" />


        <h2 className="mt-12 mb-4 text-2xl font-light tracking-tight">{t.phaseTwoTitle}</h2>
        <P>
          {t.phaseTwoBefore}
          <a href="mailto:info@rot8ion.com" className="text-[var(--viz-accent)] hover:underline">
            info [at] rot8ion.com
          </a>
          .
        </P>

        <h2 className="mt-12 mb-4 text-2xl font-light tracking-tight">{t.phaseOneTitle}</h2>
        <P>{t.phaseOne}</P>

        {workOrder.map((key) => {
          const w = t.works[key];
          return (
            <article key={key} className="mt-14 pt-10 border-t border-sitefg/10">
              <Link to={archiefLink(key)} className="block group">
                <img
                  src={workImages[key]}
                  alt={w.alt}
                  loading="lazy"
                  className="w-full h-auto rounded-sm mb-6 transition-opacity group-hover:opacity-90"
                />
              </Link>
              <h2 className="mb-4 text-2xl font-light tracking-tight">
                <Link
                  to={archiefLink(key)}
                  className="hover:text-[var(--viz-accent)] hover:underline"
                >
                  {w.title}
                </Link>
              </h2>
              {w.paragraphs.map((p, i) => (
                <P key={i}>{p}</P>
              ))}
            </article>
          );
        })}
      </ContentPage>
    </>
  );
}
